import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'

import { useUpdateOrganisation } from '@dhl/mdp-api'
import { Box, Card, Grid } from '@dhl-ui/components'
import { links } from '~/AppRoutes'

import { AccountDataTable } from '~/common/components/accountnumbers'
import { getErrorMessage } from '~/common/util/axiosUtil'

import { CancelDialog, OrganisationForm, UsersCard } from '~/modules/organisations/components'
import {
  convertToOrganisationDto,
  createOrganisationValidationSchema,
  type Organisation,
} from '~/modules/organisations/model'
import { useOrganisation } from '~/modules/organisations/hooks'
import { useNavigateWithSearchString, useNotificationMessage } from '~/common/hooks'
import { useAddressService } from '~/common/components/addresses'
import { isCccBusinessUnit } from '~/common/model/businessunits'
import { BackButton } from '~/temp-ds-component/BackButton'
import { config } from '~/config'

type Params = {
  id: string
}

export const EditOrganisationPage = () => {
  const navigateWithSearchString = useNavigateWithSearchString(true)
  const { id } = useParams<Params>()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { disableCccOrgCrud } = config

  const { organisation, isFetching, refetchOrganisation } = useOrganisation(id)
  const isCCC = isCccBusinessUnit(organisation?.businessUnitCode)
  const { addressValidationSchema, updateAddressValidationSchema } = useAddressService(
    organisation?.data.address.countryCode
  )
  const validationSchema =
    isCCC && disableCccOrgCrud ? undefined : createOrganisationValidationSchema(t, addressValidationSchema)

  const { mutateAsync: updateOrganisation } = useUpdateOrganisation()

  const onChangeCountry = async (countryCode: string) => {
    updateAddressValidationSchema(countryCode)
  }

  const onBack = (dirty: boolean) => {
    if (dirty && id) {
      setCancelDialogOpen(true)
    } else if (id) {
      navigateWithSearchString(links.organisationDetailsLink(id))
    } else {
      navigateWithSearchString(links.organisationsLink())
    }
  }

  const onSubmit = async (changedOrganisation: Organisation) => {
    const changedOrganisationDto = convertToOrganisationDto(changedOrganisation, isCCC && disableCccOrgCrud)
    if (id) {
      updateOrganisation({ id, data: changedOrganisationDto })
        .then(() => {
          showSuccess(t('admin.organisationsPage.editOrganisationSuccess'))
          navigateWithSearchString(links.organisationDetailsLink(id))
          refetchOrganisation()
        })
        .catch((error) => {
          showError(getErrorMessage(error, t('admin.organisationsPage.editOrganisationError')))
        })
    }
  }

  return isFetching || !organisation || !id ? null : (
    <>
      <Formik
        initialValues={organisation}
        onSubmit={onSubmit}
        validateOnMount
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ dirty, values }) => (
          <>
            <Box marginBottom={2}>
              <BackButton onClick={() => onBack(dirty)}>
                {t('admin.organisationsPage.organisationForm.backButtonTitle')}
              </BackButton>
            </Box>
            <Grid direction="row" container spacing={3}>
              <Grid size={{ sm: 6 }}>
                <Card>
                  <Card.Header title={t('admin.organisationsPage.organisationForm.titleText')} />
                  <Card.Content>
                    {organisation && (
                      <OrganisationForm
                        type="edit"
                        country={values.data.address.countryCode}
                        onCancel={() => onBack(dirty)}
                        onChangeCountry={onChangeCountry}
                        organisation={values}
                      />
                    )}
                  </Card.Content>
                </Card>
              </Grid>
              <Grid size={{ sm: 6 }}>
                <Box mb={2}>
                  <UsersCard organisationId={id} />
                </Box>
                {organisation && <AccountDataTable organisation={organisation} businessType={'organisation'} />}
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
      <CancelDialog
        open={cancelDialogOpen}
        onCancel={() => setCancelDialogOpen(false)}
        href={links.organisationDetailsLink(id)}
      />
    </>
  )
}
